/**
 * cytoscape-task-styles — Shared Cytoscape.js stylesheet for task DAG graphs.
 *
 * Consumed by:
 * - TaskDependencyTab (mini-DAG of dependencies/dependents)
 * - TaskWorkflowMapTab (full workflow DAG minimap)
 *
 * Each consumer can extend the base styles with tab-specific selectors.
 * Follows DAGVisualGraph.tsx patterns from @ferp/workflow-forms.
 *
 * DUMB FRONTEND: Colors from task-status-colors.ts, no hardcoded assumptions.
 */

import type { StylesheetStyle } from 'cytoscape';

// ============================================================================
// BASE NODE STYLES
// ============================================================================

const BASE_NODE: StylesheetStyle = {
  selector: 'node',
  style: {
    'background-color': 'data(bg)',
    'border-width': 2,
    'border-color': 'data(borderColor)',
    'label': 'data(label)',
    'text-valign': 'center' as const,
    'text-halign': 'center' as const,
    'font-size': 11,
    'color': '#212529',
    'width': 50,
    'height': 50,
    'text-wrap': 'wrap' as const,
    'text-max-width': '70px',
  },
};

const CURRENT_NODE: StylesheetStyle = {
  selector: 'node[?isCurrent]',
  style: {
    'border-width': 4,
    'border-color': '#1c7ed6',
    'width': 65,
    'height': 65,
    'font-size': 12,
    'font-weight': 'bold',
    'overlay-opacity': 0.15,
    'overlay-color': '#1c7ed6',
  },
};

// ============================================================================
// BASE EDGE STYLES
// ============================================================================

const BASE_EDGE: StylesheetStyle = {
  selector: 'edge',
  style: {
    'width': 2,
    'line-color': '#adb5bd',
    'target-arrow-color': '#868e96',
    'target-arrow-shape': 'triangle' as const,
    'curve-style': 'bezier' as const,
    'arrow-scale': 1.2,
  },
};

// ============================================================================
// PUBLIC API
// ============================================================================

/** Base stylesheet shared by all task Cytoscape graphs */
export const CYTOSCAPE_BASE_STYLES: StylesheetStyle[] = [
  BASE_NODE,
  CURRENT_NODE,
  BASE_EDGE,
];

/** Dependency-specific edge styles (upstream/downstream) */
export const DEPENDENCY_EDGE_STYLES: StylesheetStyle[] = [
  {
    selector: 'edge[?isUpstream]',
    style: {
      'line-color': '#74c0fc',
      'target-arrow-color': '#4dabf7',
    },
  },
  {
    selector: 'edge[?isDownstream]',
    style: {
      'line-color': '#69db7c',
      'target-arrow-color': '#51cf66',
    },
  },
];

/** Workflow-map-specific edge styles (active path highlighting) */
export const WORKFLOW_MAP_EDGE_STYLES: StylesheetStyle[] = [
  {
    selector: 'edge[?isActive]',
    style: {
      'line-color': '#4dabf7',
      'target-arrow-color': '#339af0',
      'width': 2.5,
    },
  },
];

/**
 * Build the full stylesheet for a specific graph type.
 * Merges base styles with tab-specific overrides.
 */
export function buildTaskCytoscapeStyles(
  ...additionalStyles: StylesheetStyle[][]
): StylesheetStyle[] {
  return [...CYTOSCAPE_BASE_STYLES, ...additionalStyles.flat()];
}
